// BeaconPanel component for displaying and selecting test beacons

import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../core/Icon';
import { TEST_BEACONS } from '../constants';
import {
  Panel,
  PanelTitle,
  BeaconListContainer,
  BeaconItemContainer,
  BeaconId,
  BeaconLocationText,
  PatientAssignment,
  Button,
} from '../styles';
import type { BeaconToPatientMap, GatewayRoomMap, Staff, Physician, Anesthesiologist } from '../types';

interface BeaconPanelProps {
  organizationId: string;
  selectedBeacon: string | null;
  beaconToPatientMap: BeaconToPatientMap;
  beaconToStaffMap: Record<string, Staff>;
  beaconToPhysicianMap: Record<string, Physician>;
  beaconToAnesthesiologistMap: Record<string, Anesthesiologist>;
  gatewayRoomMap: GatewayRoomMap;
  onSelectBeacon: (beaconId: string | null) => void;
  onMoveOffsite: (beaconId: string) => void;
  getBeaconLocationDisplay: (beaconId: string) => string | null;
}

export const BeaconPanel: React.FC<BeaconPanelProps> = ({
  organizationId,
  selectedBeacon,
  beaconToPatientMap,
  beaconToStaffMap,
  beaconToPhysicianMap,
  beaconToAnesthesiologistMap,
  onSelectBeacon,
  onMoveOffsite,
  getBeaconLocationDisplay,
}) => (
  <Panel>
    <PanelTitle>Test Beacons</PanelTitle>
    <BeaconListContainer>
      {TEST_BEACONS.map(beacon => {
        const location = getBeaconLocationDisplay(beacon.id);
        const isSelected = selectedBeacon === beacon.id;
        const assignedPatient = beaconToPatientMap[beacon.id];

        return (
          <BeaconItemContainer
            key={beacon.id}
            color={beacon.color}
            active={isSelected}
            onClick={() => onSelectBeacon(isSelected ? null : beacon.id)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>{beacon.label}</strong>
              {isSelected && (
                <Icon className="material-icons" style={{ fontSize: '1rem', color: '#4CAF50' }}>
                  check
                </Icon>
              )}
            </div>
            <BeaconId>{beacon.id}</BeaconId>
            <BeaconLocationText offsite={!location}>{location || 'Off-site'}</BeaconLocationText>
            {assignedPatient && (
              <PatientAssignment>
                <Link
                  to={`/su/${organizationId}/patients/${assignedPatient.id}`}
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  → {assignedPatient.name}
                </Link>
              </PatientAssignment>
            )}
            {beaconToStaffMap[beacon.id] && (
              <PatientAssignment>
                <Link
                  to={`/su/${organizationId}/staff/${beaconToStaffMap[beacon.id].staffId}`}
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  → {beaconToStaffMap[beacon.id].name}
                </Link>
              </PatientAssignment>
            )}
            {beaconToPhysicianMap[beacon.id] && (
              <PatientAssignment>
                <Link
                  to={`/su/${organizationId}/physicians/${beaconToPhysicianMap[beacon.id].id}`}
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  → {beaconToPhysicianMap[beacon.id].name}
                </Link>
              </PatientAssignment>
            )}
            {beaconToAnesthesiologistMap[beacon.id] && (
              <PatientAssignment>
                <Link
                  to={`/su/${organizationId}/anesthesiologists/${beaconToAnesthesiologistMap[beacon.id].id}`}
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  → {beaconToAnesthesiologistMap[beacon.id].name}
                </Link>
              </PatientAssignment>
            )}
          </BeaconItemContainer>
        );
      })}
    </BeaconListContainer>

    {selectedBeacon && (
      <Button danger style={{ marginTop: '1rem', width: '100%' }} onClick={() => onMoveOffsite(selectedBeacon)}>
        <Icon className="material-icons">signal_wifi_off</Icon>
        Move Off-site
      </Button>
    )}
  </Panel>
);
